import { FC, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Link,
  IconButton,
} from "@mui/material";
import {
  EmojiEvents,
  AutoAwesome,
  RocketLaunch,
  Close,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAtom } from "jotai";
import { sessionAtom } from "../../features/accounts/account.state";
import { EntityType } from "../../features/data/data-types";
import { trackEvent } from "../../features/analytics/analytics";
import { trackIntercomWall } from "../../services/intercom";
import {
  helpPanelStateAtom,
  copilotPrefillAtom,
  suppressConciergeHandoffAtom,
} from "../../containers/copilot/copilotAtoms";
import {
  getNextTierInfo,
  getTierForProducts,
  getPlanExpansion,
  getLimitLabel,
  getAccomplishmentText,
  getManageUrl,
  getManageLabel,
  TierOption,
} from "./exhaustion-helpers";
import {
  ANNUAL_DISCOUNT_PERCENT,
  annualMonthlyPrice,
  formatPrice,
} from "../../features/billing/plan";
import {
  defaultReturnUrl,
  isInternalPath,
} from "../../features/billing/upgradeReturn";

export type ExhaustionMode = "modal" | "inline";

export interface ExhaustionWallProps {
  /** The type of limit being hit */
  type: EntityType;
  /** Current usage count */
  current: number;
  /** Plan limit */
  limit: number;
  /** Display mode */
  mode: ExhaustionMode;
  /** Modal open state (modal mode only) */
  isOpen?: boolean;
  /** Close handler */
  onClose?: () => void;
  /** Custom title override */
  title?: string;
  /** Custom subtitle override */
  subtitle?: string;
  /** Show "Buy Credits" instead of "Upgrade" (for credits) */
  onBuyCredits?: () => void;
  /** Surface identifier for analytics (e.g. "product-grid-add-button") */
  entryPoint?: string;
  /** Plan id (e.g., "lite", "pro") — when set and it matches the skip tier,
   *  promote that tier as the primary upgrade target so the modal frames
   *  the plan the caller actually pointed the user at. Used by Shopify's
   *  dual-CTA upsell where each button targets a specific tier. */
  targetPlan?: string;
  /** Internal path checkout should return to. Absent = the page the wall is on (see `defaultReturnUrl`). */
  returnUrl?: string;
  /** Products the target plan must cover; deep-links as `?products=` so the card opens pre-sized. */
  targetProducts?: number;
  /**
   * Runs when the user commits to an upgrade, before navigation. The import
   * surfaces use it to keep their chosen file for the return trip.
   */
  onUpgradeClick?: () => void;
}

const BILLING_URL = "/account/billing/plan";

export const ExhaustionWall: FC<ExhaustionWallProps> = ({
  type,
  current,
  limit,
  mode,
  isOpen,
  onClose,
  title,
  subtitle,
  onBuyCredits,
  entryPoint,
  targetPlan,
  returnUrl,
  targetProducts,
  onUpgradeClick,
}) => {
  const [session] = useAtom(sessionAtom);
  const [, setHelpPanelState] = useAtom(helpPanelStateAtom);
  const [, setCopilotPrefill] = useAtom(copilotPrefillAtom);
  const [, setSuppressConciergeHandoff] = useAtom(suppressConciergeHandoffAtom);
  const navigate = useNavigate();
  const location = useLocation();

  // HelpButton (which hosts the Copilot panel that consumes copilotPrefillAtom) is
  // excluded from /concierge — showing "Ask AI" there sends the prefill into a void,
  // or it surfaces in Concierge unintentionally. Hide the link on that route; the
  // user already has an AI context on that page.
  const askAiHidden = location.pathname.startsWith("/concierge");

  const pct = limit > 0 ? Math.min((current / limit) * 100, 100) : 100;
  const isExhausted = current >= limit;
  const overBy = current > limit ? current - limit : 0;

  const nextTier = useMemo(() => {
    const base = getNextTierInfo(session.account?.planCode, type);
    if (!base) return null;
    // When the caller pointed the user at a specific tier (e.g., Shopify's
    // dual-CTA "Pro $49"), promote that tier as the primary so the modal
    // doesn't keep pushing the default next tier. Currently only the
    // skipTier path needs swapping — if targetPlan === base.id it's
    // already the primary.
    if (targetPlan && base.skipTier?.id === targetPlan) {
      // Promote skipTier; drop further skip option since user explicitly
      // chose to skip ahead.
      return { ...base.skipTier, skipTier: undefined };
    }
    return base;
  }, [session.account?.planCode, type, targetPlan]);

  // Capacity the user can buy on the plan they already have (Pro's sliders).
  // When this exists it REPLACES the next-tier pitch outright: the answer to
  // "you filled your seats" is the $5 seat on their own plan, and offering
  // Premium beside it just reintroduces the thing this fixes. Null on every
  // other plan and every credits wall, so nothing else changes shape.
  const expansion = useMemo(
    () => getPlanExpansion(session.account?.planCode, type, limit),
    [session.account?.planCode, type, limit],
  );

  /**
   * The tier pitch, suppressed while a same-plan expansion is on offer.
   *
   * When the caller sized the job (`targetProducts`, CSV and Shopify imports), quote the tier AT that
   * size. The subtitle already says "Pro with 1,000 products imports all of them"; following it with
   * "from $49/mo for 500 products" quoted a plan that leaves half the file behind, while checkout
   * opened at the sized price anyway.
   */
  const tierPitch = useMemo(() => {
    if (expansion || !nextTier) return null;
    if (!(targetProducts && targetProducts > 0) || nextTier.id !== targetPlan) return nextTier;
    const sized = getTierForProducts(session.account?.planCode, targetProducts);
    if (!sized || sized.short || sized.id !== nextTier.id) return nextTier;
    return {
      ...nextTier,
      price: sized.price,
      fromPrice: false,
      limitLabel: `${sized.products.toLocaleString()} products`,
    };
  }, [expansion, nextTier, targetProducts, targetPlan, session.account?.planCode]);

  const accomplishment = getAccomplishmentText(type, current);
  const limitLabel = getLimitLabel(type, limit);

  // P2.4: Escape hatch — only surface for entities with a natural manage page
  const manageUrl = getManageUrl(type);
  const manageLabel = getManageLabel(type);
  const showManageLink = !!manageUrl && isExhausted && mode === "modal";

  // Extended baseline metadata for segmented funnel analysis.
  // All fields land in Analytics.Events.Metadata (JSON) and are queryable via JSON_VALUE.
  const account = session.account;
  const summary = account?.summary;
  const daysSinceSignup = account?.created
    ? Math.floor(
        (Date.now() - new Date(account.created).getTime()) / 86_400_000,
      )
    : undefined;

  const baseMetadata = {
    current,
    limit,
    plan: account?.planCode,
    mode,
    is_at_limit: current >= limit,
    entry_point: entryPoint,
    // Lifecycle + value signals (added for P0/P1 baseline window)
    days_since_signup: daysSinceSignup,
    products_count: summary?.itemsCount ?? 0,
    catalogs_count: summary?.catalogsCount ?? 0,
    has_catalog: (summary?.catalogsCount ?? 0) > 0,
    has_published: (summary?.publishedCatalogs ?? 0) > 0,
    concierge_sessions: summary?.concierge?.totalSessions ?? 0,
    country: account?.country,
    gateway: account?.gateway,
    is_paying: account?.isPaying ?? false,
  };

  // P0.1: useEffect (not useMemo) so the shown event is a true side-effect
  useEffect(() => {
    if (mode === "modal" && !isOpen) return;
    trackEvent({
      // "feature" (not "trace") so the event lands in Analytics.Events — trace events
      // are treated as diagnostics server-side and skipped by AnalyticsService.
      // Keeps `internal: true` so GA/Sentry don't receive these low-value renders.
      type: "feature",
      category: "exhaustion-wall",
      action: "shown",
      label: type,
      metadata: baseMetadata,
      internal: true,
    });
    trackIntercomWall(type, entryPoint, account?.planCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode]);

  const upgradeMetadata = (tier: TierOption, level: "next" | "skip") => ({
    ...baseMetadata,
    target_plan: tier.id,
    target_price: tier.price,
    upgrade_level: level,
  });

  const handleUpgradeClick = (tier: TierOption, level: "next" | "skip") => {
    trackEvent({
      type: "conversion",
      category: "exhaustion-wall",
      action: "upgrade-click",
      label: type,
      metadata: upgradeMetadata(tier, level),
    });
    // Explicit close + navigate (mirrors handleManageClick pattern). Previously
    // these buttons used `component={RouterLink} to={...} onClick={onClose}`
    // which leaves a race between RouterLink's internal navigation and the
    // setState(null) in onClose — the modal could stay open over the billing
    // page after the user clicked Upgrade. Driving navigation manually after
    // close fixes the ordering.
    onClose?.();
    onUpgradeClick?.();
    // Size the target card when the caller knows how big the job is (S79: the store's product count).
    //
    // Only for the tier it was SIZED FOR. `targetProducts` is computed against one specific plan, so
    // attaching it to a different one quotes that plan a capacity nobody asked for: clicking "jump to
    // Pro" from a wall sized to Lite produced `plan=pro&products=100`, i.e. Pro asked to hold 100.
    // It happened to be harmless (PlanCard snaps up to Pro's 500 base either way) and that is exactly
    // why it would have survived — a wrong number that currently rounds to the right one.
    const sizeParam =
      targetProducts > 0 && tier.id === targetPlan ? `&products=${targetProducts}` : "";
    navigate(`${BILLING_URL}?plan=${tier.id}${sizeParam}${returnParam()}`);
  };

  // Every wall carries a return URL, so checkout sends the user back to the thing they were doing
  // rather than leaving them on the billing page thinking it was lost. An explicit `returnUrl` from
  // the caller wins; with none set, the page the wall is on. Until 2026-09-30 only Concierge sessions
  // returned, and every CSV, grid, PDF-import and catalog wall landed on billing: that landing was the
  // one factor common to all five traced day-one paid cancels (S34 § 8, gap #2).
  const returnParam = () => {
    const target = isInternalPath(returnUrl)
      ? returnUrl
      : defaultReturnUrl(location);
    return target ? `&returnUrl=${encodeURIComponent(target)}` : "";
  };

  /** The billing page with the return URL attached, for the CTAs that name no tier. */
  const goToBilling = () => {
    onClose?.();
    onUpgradeClick?.();
    const param = returnParam();
    navigate(param ? `${BILLING_URL}?${param.slice(1)}` : BILLING_URL);
  };

  // Same-plan expansion: send them to their OWN plan card, where the sliders
  // are. `?plan=` highlights that card (ChangePlan reads it for `highlight`),
  // so they land looking at the control that solves it.
  const handleExpandClick = () => {
    if (!expansion) return;
    trackEvent({
      type: "conversion",
      category: "exhaustion-wall",
      action: "expand-click",
      label: type,
      metadata: {
        ...baseMetadata,
        target_plan: expansion.planId,
        max_limit: expansion.maxLimit,
        unit_price: expansion.unitPrice,
      },
    });
    onClose?.();
    onUpgradeClick?.();
    navigate(`${BILLING_URL}?plan=${expansion.planId}${returnParam()}`);
  };

  /** "up to 6 users, at $5 per extra user per month" */
  const expansionOffer = expansion
    ? `up to ${expansion.maxLabel}${
        expansion.unitPrice
          ? `, at $${formatPrice(expansion.unitPrice)} per extra ${expansion.unitNoun} per month`
          : ""
      }`
    : "";

  // P0.6: separate dismiss handlers with dismiss_reason
  const handleMaybeLater = () => {
    trackEvent({
      type: "feature",
      category: "exhaustion-wall",
      action: "dismiss",
      label: type,
      metadata: { ...baseMetadata, dismiss_reason: "maybe_later" },
      internal: true,
    });
    onClose?.();
  };

  const handleCloseX = () => {
    trackEvent({
      type: "feature",
      category: "exhaustion-wall",
      action: "dismiss",
      label: type,
      metadata: { ...baseMetadata, dismiss_reason: "close_x" },
      internal: true,
    });
    onClose?.();
  };

  const handleBackdropClose = () => {
    trackEvent({
      type: "feature",
      category: "exhaustion-wall",
      action: "dismiss",
      label: type,
      metadata: { ...baseMetadata, dismiss_reason: "backdrop" },
      internal: true,
    });
    onClose?.();
  };

  const handleAskCopilot = () => {
    trackEvent({
      type: "feature",
      category: "exhaustion-wall",
      action: "ask-copilot",
      label: type,
      metadata: baseMetadata,
      internal: true,
    });
    // P1.4: decision-oriented prefill
    const prefill = `I hit my ${type} limit (${current}/${limit} on ${session.account?.planName ?? "my"} plan). Which plan fits me best, and is there a way to keep working without upgrading right now?`;
    // Suppress the Copilot→Concierge handoff for this message — a plan-help
    // question shouldn't be auto-re-sent by Concierge (would waste credits).
    setSuppressConciergeHandoff(true);
    setCopilotPrefill(prefill);
    setHelpPanelState("panel");
    onClose?.();
  };

  const handleBuyCredits = () => {
    trackEvent({
      type: "conversion",
      category: "exhaustion-wall",
      action: "buy-credits-click",
      label: type,
      metadata: baseMetadata,
    });
    onBuyCredits?.();
  };

  // P2.4: Escape hatch click — closes modal, navigates to manage page
  const handleManageClick = () => {
    if (!manageUrl) return;
    trackEvent({
      type: "feature",
      category: "exhaustion-wall",
      action: "alternative-action-click",
      label: type,
      metadata: { ...baseMetadata, alternative: "manage", manage_url: manageUrl },
      internal: true,
    });
    onClose?.();
    navigate(manageUrl);
  };

  // "from" because a Pro tier's price is a floor: its sliders add per-unit
  // charges on top of the base. Lite/Premium have no sliders, so they are exact.
  const fromPrefix = (tier: TierOption) => (tier.fromPrice ? "from " : "");

  const priceSuffix = (tier: TierOption) =>
    tier.price > 0
      ? ` — ${fromPrefix(tier)}$${formatPrice(tier.price)}/mo`
      : "";

  /** The same price billed yearly. No "from" prefix — the caller's already carries it. */
  const yearlyAmount = (tier: TierOption) =>
    `$${formatPrice(annualMonthlyPrice(tier.price))}/mo`;

  // Inline mode is rendered inside an <Alert> which already shows its own severity icon,
  // so the accomplishment-header icon would appear duplicated. Only render it in modal mode.
  const showHeaderIcon = mode === "modal";

  const content = (
    <Box>
      {/* Accomplishment header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        {showHeaderIcon &&
          (isExhausted ? (
            <EmojiEvents sx={{ color: "warning.main", fontSize: 28 }} />
          ) : (
            <RocketLaunch sx={{ color: "primary.main", fontSize: 28 }} />
          ))}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title || accomplishment}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {limitLabel}
          </Typography>
          {/* P0.3: show "(X over)" when over limit */}
          <Typography variant="caption" color="text.secondary">
            {current}/{limit}
            {overBy > 0 && ` (${overBy} over)`}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "action.hover",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              bgcolor: isExhausted
                ? "error.main"
                : pct > 80
                  ? "warning.main"
                  : "primary.main",
            },
          }}
        />
      </Box>

      {/* Context message */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {subtitle ||
          (isExhausted
            ? `Your ${session.account?.planName ?? "current"} plan includes ${limitLabel.toLowerCase()}.`
            : `You're approaching your plan limit of ${limitLabel.toLowerCase()}.`)}
        {expansion && (
          <>
            {" "}
            You can add more on this plan, <b>{expansionOffer}</b>.
          </>
        )}
        {tierPitch && (
          <>
            {" "}
            {/* P2.1: price next to tier name */}
            Upgrade to <b>{tierPitch.name}</b>
            {priceSuffix(tierPitch)} for <b>{tierPitch.limitLabel}</b>.
          </>
        )}
      </Typography>

      {/* S52: the button labels and the sentence above quote the monthly price,
          but the plans page now opens on yearly. Say the yearly price here so
          what the user was quoted matches what they land on. */}
      {tierPitch && tierPitch.price > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Or {fromPrefix(tierPitch)}
          <b>{yearlyAmount(tierPitch)}</b> billed yearly, saving{" "}
          {ANNUAL_DISCOUNT_PERCENT}%.
        </Typography>
      )}

      {/* P1.3: Reassurance copy */}
      {isExhausted && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1, fontStyle: "italic" }}
        >
          Your work is safe. You can continue editing what you already have.
        </Typography>
      )}

      {/* P1.4: "Ask AI which plan fits you" — hidden on /concierge (see askAiHidden above) */}
      {!askAiHidden && (
        <Box sx={{ mt: 1 }}>
          <Link
            component="button"
            variant="body2"
            onClick={handleAskCopilot}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <AutoAwesome sx={{ fontSize: 16 }} />
            Ask AI which plan fits you
          </Link>
        </Box>
      )}
    </Box>
  );

  // -- INLINE MODE --
  if (mode === "inline") {
    return (
      <Alert
        severity={isExhausted ? "warning" : "info"}
        icon={isExhausted ? <EmojiEvents /> : <RocketLaunch />}
        sx={{ mb: 2, alignItems: "flex-start" }}
        action={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {onBuyCredits && (
              <Button
                variant="contained"
                size="small"
                onClick={handleBuyCredits}
                sx={{ whiteSpace: "nowrap" }}
              >
                Buy Credits
              </Button>
            )}
            {/* Same-plan capacity comes first and stands alone — see `expansion`. */}
            {expansion && (
              <Button
                variant="contained"
                size="small"
                onClick={handleExpandClick}
                sx={{ whiteSpace: "nowrap" }}
              >
                {`Add ${expansion.unitNoun}s`}
              </Button>
            )}
            {/* Always show Upgrade as alternative when a next tier exists.
                For credits walls this runs alongside Buy Credits (subscription = recurring allocation). */}
            {tierPitch && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleUpgradeClick(tierPitch, "next")}
                sx={{ whiteSpace: "nowrap" }}
              >
                {`Upgrade${priceSuffix(tierPitch)}`}
              </Button>
            )}
            {/* Fallback when there's neither a tier nor a top-up path (Premium user). */}
            {!tierPitch && !expansion && !onBuyCredits && (
              <Button
                variant="outlined"
                size="small"
                onClick={goToBilling}
                sx={{ whiteSpace: "nowrap" }}
              >
                Upgrade
              </Button>
            )}
          </Box>
        }
      >
        {content}
      </Alert>
    );
  }

  // -- MODAL MODE --
  return (
    <Dialog
      open={!!isOpen}
      onClose={handleBackdropClose}
      maxWidth="sm"
      fullWidth
    >
      {/* P0.4: Close X in DialogTitle */}
      <DialogTitle sx={{ pb: 0, position: "relative" }}>
        <IconButton
          onClick={handleCloseX}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {content}
        {/* P1.2: Multi-benefit box (modal only) */}
        {tierPitch && tierPitch.benefits.length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Upgrading to {tierPitch.name}
              {priceSuffix(tierPitch)} also unlocks:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
              {tierPitch.benefits.map((b, i) => (
                <Typography component="li" variant="body2" key={i}>
                  {b}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* P2.2: Skip-level option when a 2-above tier exists */}
        {tierPitch?.skipTier && (
          <Box sx={{ mt: 1.5 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() =>
                tierPitch.skipTier &&
                handleUpgradeClick(tierPitch.skipTier, "skip")
              }
              sx={{ fontWeight: 500 }}
            >
              Or jump to {tierPitch.skipTier.name}
              {priceSuffix(tierPitch.skipTier)}
              {tierPitch.skipTier.price > 0 &&
                ` (${yearlyAmount(tierPitch.skipTier)} yearly)`}{" "}
              — {tierPitch.skipTier.limitLabel}
            </Link>
          </Box>
        )}

        {/* P2.4: Escape hatch — only when exhausted AND type has a manage page */}
        {showManageLink && (
          <Box sx={{ mt: 1.5 }}>
            <Link
              component="button"
              variant="body2"
              onClick={handleManageClick}
              sx={{ color: "text.secondary" }}
            >
              {manageLabel} →
            </Link>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleMaybeLater} color="inherit">
          Maybe Later
        </Button>
        {onBuyCredits ? (
          <Button variant="contained" onClick={handleBuyCredits}>
            Buy Credits
          </Button>
        ) : expansion ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleExpandClick}
          >
            {`Add ${expansion.unitNoun}s on ${expansion.planName}`}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (tierPitch) {
                handleUpgradeClick(tierPitch, "next");
              } else {
                goToBilling();
              }
            }}
          >
            {tierPitch ? `Upgrade to ${tierPitch.name}` : "Upgrade Plan"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
